// apps/client/src/components/AIChat/MessageInput.tsx

import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';

interface MessageInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Text input for sending messages to AI
 * Supports Enter to send, Shift+Enter for newlines
 * Auto-expands as user types
 */
export function MessageInput({
  onSend,
  disabled = false,
  placeholder = "Ask about your commissions...",
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea as content changes
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '40px'; // Reset to min height
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, 120)}px`; // Max 120px
    }
  }, [message]);

  const handleSend = () => {
    const trimmed = message.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setMessage('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-gray-700 p-3 bg-gray-800">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={`
            flex-1 resize-none rounded-lg border px-3 py-2
            text-sm focus:outline-none focus:ring-2 focus:border-transparent
            overflow-y-auto
            ${disabled
              ? 'bg-gray-700 border-gray-600 text-gray-500 cursor-not-allowed placeholder-gray-600'
              : 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-orange-500'
            }
          `}
          style={{
            minHeight: '40px',
            maxHeight: '120px',
            height: '40px',
          }}
        />

        <button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          className={`
            flex-shrink-0 rounded-lg p-2 transition-all duration-200
            ${
              disabled || !message.trim()
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
            }
          `}
          title="Send message (Enter)"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>

      <p className="text-xs text-gray-400 mt-1.5">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}
